// Commercial-terms footnotes disclosed on an offer.
//
// Two kinds of disclosure ride the same channel so every offer surface (page,
// PDF, email, admin builder) renders them one way:
//
//   • vat_exclusive — a constant: Future Demand quotes net B2B prices and Stripe
//     adds VAT at invoice time (automatic_tax). Always present.
//   • ad_buy_fee    — plan-scoped: a % fee on managed ad spend carried by the
//     catalog plan (today Prisma Core+ = 5). Sourced from the plan's
//     `adBuyFeePct` attribute, disclosed here, and — at signing — seeded into
//     the org's ad-spend-share term so billing matches what the buyer saw.
//
// The resolver is pure (offer lines + published catalog → footnotes) so the same
// function feeds the server-side snapshot freeze and the admin builder's live
// preview. Canonical text is English; the customer app localizes by `code`.

import type { CatalogSnapshot } from "./catalog";

export type CommercialFootnoteCode = "vat_exclusive" | "ad_buy_fee";

export interface CommercialFootnote {
  code: CommercialFootnoteCode;
  /** Canonical English text — rendered verbatim on server surfaces (PDF, email)
   * and stored in the signed snapshot for audit fidelity. The customer app may
   * re-render from `code`/`params` for localization. */
  text: string;
  /** Structured params for client-side localization (absent for constant notes). */
  params?: Record<string, string | number>;
}

/** Frozen into the offer snapshot (v2) alongside `entitlements`. */
export interface OfferCommercialTerms {
  schema: 1;
  footnotes: CommercialFootnote[];
}

/** Minimal offer line: enough to identify the plan for footnote resolution. */
export interface CommercialLineRef {
  family: "live" | "prisma" | "affinity" | null;
  plan_key: string | null;
}

export const VAT_EXCLUSIVE_TEXT = "All prices exclude VAT.";

/** "5" / "2.5" — no trailing zeros; used in the English footnote text. */
function fmtPct(pct: number): string {
  return String(pct);
}

export function adBuyFeeText(planName: string, pct: number, minimumEur?: number, minimumUsd?: number): string {
  if (minimumUsd != null) {
    return `${planName} includes a ${fmtPct(pct)}% fee on actual managed ad spend, with a $${minimumUsd} USD minimum service fee per campaign. The higher amount applies.`;
  }
  if (minimumEur != null) {
    return `${planName} includes a ${fmtPct(pct)}% fee on actual managed ad spend, with a €${minimumEur} minimum service fee per campaign. The higher amount applies.`;
  }
  return `${planName} includes a ${fmtPct(pct)}% fee on managed ad spend.`;
}

export interface AdBuyFee {
  pct: number;
  planKey: string;
  planName: string;
  minimumEur?: number;
  minimumUsd?: number;
}

const prismaPlan = (
  catalog: CatalogSnapshot,
  planKey: string,
): { name: string; adBuyFeePct?: number; adBuyFeeMinimumEur?: number; adBuyFeeMinimumUsd?: number } | undefined =>
  (catalog.prisma as Record<string, { name: string; adBuyFeePct?: number; adBuyFeeMinimumEur?: number; adBuyFeeMinimumUsd?: number } | undefined>)[planKey];

/** Historical catalogs charged their EUR floor even for USD subscriptions.
 * Keep that behavior when the USD floor has not been published. */
function minimumForCurrency(plan: NonNullable<ReturnType<typeof prismaPlan>>, currency: string) {
  return currency.toUpperCase() === "USD" && plan.adBuyFeeMinimumUsd != null
    ? { minimumUsd: plan.adBuyFeeMinimumUsd }
    : plan.adBuyFeeMinimumEur != null ? { minimumEur: plan.adBuyFeeMinimumEur } : {};
}

/**
 * Resolve the footnotes disclosed on an offer: the constant VAT note first, then
 * one per distinct plan that carries an ad-buy fee (in first-appearance order).
 * Deterministic ordering keeps the frozen snapshot hash stable.
 */
export function resolveCommercialFootnotes(
  lines: CommercialLineRef[],
  catalog: CatalogSnapshot,
  currency: string = "EUR",
): CommercialFootnote[] {
  const out: CommercialFootnote[] = [{ code: "vat_exclusive", text: VAT_EXCLUSIVE_TEXT }];
  const seen = new Set<string>();
  for (const li of lines) {
    if (li.family !== "prisma" || !li.plan_key || seen.has(li.plan_key)) continue;
    const plan = prismaPlan(catalog, li.plan_key);
    if (!plan || plan.adBuyFeePct == null) continue;
    seen.add(li.plan_key);
    const minimum = minimumForCurrency(plan, currency);
    out.push({
      code: "ad_buy_fee",
      text: adBuyFeeText(plan.name, plan.adBuyFeePct, minimum.minimumEur, minimum.minimumUsd),
      params: {
        planKey: li.plan_key, planName: plan.name, pct: plan.adBuyFeePct,
        ...(minimum.minimumEur != null ? { minimumEur: minimum.minimumEur } : {}),
        ...(minimum.minimumUsd != null ? { minimumUsd: minimum.minimumUsd } : {}),
      },
    });
  }
  return out;
}

/**
 * The ad-buy fee (%) to bill for an offer's lines, if any. Returns the highest
 * fee found (defensive — today only one plan carries one), with the plan it came
 * from. Drives the ad-spend-share seed at signing.
 */
export function adBuyFeeForLines(
  lines: CommercialLineRef[],
  catalog: CatalogSnapshot,
  currency: string = "EUR",
): AdBuyFee | null {
  let best: AdBuyFee | null = null;
  for (const li of lines) {
    if (li.family !== "prisma" || !li.plan_key) continue;
    const plan = prismaPlan(catalog, li.plan_key);
    if (!plan || plan.adBuyFeePct == null) continue;
    if (!best || plan.adBuyFeePct > best.pct) {
      best = {
        pct: plan.adBuyFeePct, planKey: li.plan_key, planName: plan.name,
        ...minimumForCurrency(plan, currency),
      };
    }
  }
  return best;
}

/**
 * A percentage → the exact decimal-string FRACTION `org_ad_spend_terms.percentage`
 * stores (5 → "0.05", 2.5 → "0.025", 10 → "0.1"). Done by string math, never
 * float division, so no IEEE-754 drift can reach an invoice — MVR re-parses this
 * with Decimal and bills spend × percentage.
 */
export function pctToFraction(pct: number): string {
  const s = String(pct);
  const neg = s.startsWith("-");
  const [intPart, fracPart = ""] = (neg ? s.slice(1) : s).split(".");
  const digits = intPart + fracPart;
  const pointFromRight = fracPart.length + 2; // /100 shifts the point two more places
  const padded = digits.padStart(pointFromRight + 1, "0");
  const cut = padded.length - pointFromRight;
  const intOut = padded.slice(0, cut).replace(/^0+(?=\d)/, "");
  const fracOut = padded.slice(cut).replace(/0+$/, "");
  const body = fracOut ? `${intOut}.${fracOut}` : intOut;
  return neg && body !== "0" ? `-${body}` : body;
}
